import { Lightning, Img, Colors, Registry } from "@lightningjs/sdk";
import { List } from "@lightningjs/ui";
import theme from "../../../../lib/theme";
import { MENU_WIDTH } from "../../../../lib/utils";
import { ChannelListBase, LiveTvGroup } from "../ChannelListBase/ChannelListBase";
import { ChannelItemModel } from "../../../../lib/models";
import { MediaPoster } from "../../../../components/data-display/MediaPoster/MediaPoster";
import configs from "../../../../lib/configs";

const PREVIEW_W = 610; // matches _createChannelItem w
const PREVIEW_H = Math.round((PREVIEW_W * 9) / 16); // 343
const PREVIEW_GAP = 12;
const ITEM_SPACING = 15;
const SCROLL_AFTER = 2; // must match scroll: { after: N } in the List template
const LOGO_W = 300;
const LOGO_H = Math.round((LOGO_W * 9) / 16);

interface ChannelListHorizontalSpec extends Lightning.Component.TemplateSpec {
    Container: {
        List: typeof List;
    };
    Preview: {
        Placeholder: {
            Logo: typeof MediaPoster;
        };
        Image: object;
    };
}

export class ChannelListHorizontal
    extends ChannelListBase
    implements Lightning.Component.ImplementTemplateSpec<ChannelListHorizontalSpec>
{
    private _previewTxHandler!: (...args: any[]) => void;
    private _previewTxErrorHandler!: (...args: any[]) => void;
    private _previewRetryCount = 0;
    private _previewUrl: string | null = null;
    private _previewRetryTimeout: number | null = null;

    static override _template(): Lightning.Component.Template<ChannelListHorizontalSpec> {
        return {
            w: theme.layout.w - MENU_WIDTH - 28,
            h: theme.channelItem.h + 48,
            collision: true,
            zIndex: 10,
            Preview: {
                x: 0,
                y: -(PREVIEW_H + PREVIEW_GAP),
                w: PREVIEW_W,
                h: PREVIEW_H,
                alpha: 0,
                zIndex: 50,
                rect: true,
                color: Colors(theme.colors.bg.secondary).get(),
                shader: { type: Lightning.shaders.RoundedRectangle, radius: 8 },
                transitions: { alpha: { duration: 0.2 } },
                Placeholder: {
                    w: PREVIEW_W,
                    h: PREVIEW_H,
                    Logo: {
                        type: MediaPoster,
                        useBorder: false,
                        mount: 0.5,
                        x: PREVIEW_W / 2,
                        y: PREVIEW_H / 2,
                        w: LOGO_W,
                        h: LOGO_H
                    }
                },
                Image: {
                    w: PREVIEW_W,
                    h: PREVIEW_H,
                    alpha: 0.01,
                    transitions: { alpha: { duration: 0.3 } }
                }
            },
            Container: {
                w: theme.layout.w - MENU_WIDTH,
                h: theme.channelItem.h + 48,
                x: 0,
                y: 0,
                clipping: true,
                collision: true,
                List: {
                    type: List,
                    collision: true,
                    direction: "row",
                    w: theme.layout.w - MENU_WIDTH,
                    h: theme.channelItem.h + 48,
                    y: 0,
                    x: 0,
                    scroll: { after: 2 },
                    gcThreshold: 24,
                    requestThreshold: 20,
                    enableRequests: true,
                    signals: { onRequestItems: true },
                    scrollTransition: {
                        duration: 0.3,
                        timingFunction: "cubic-bezier(0.4, 0, 0.2, 1)"
                    }
                }
            }
        };
    }

    readonly Container = this.getByRef("Container")!;

    protected override get _list(): List {
        return this.Container.getByRef("List")! as List;
    }

    // Group titles don't make sense in a horizontal strip — flatten to channels only
    override addGroups(groups: LiveTvGroup[]): void {
        this._groups = groups;
        this._channels = groups.flatMap((g) => g.channels);
        let i = 0;
        this._list.reload(
            groups.flatMap((g) =>
                g.channels.map((ch) => ({ ...this._createChannelItem(ch, i++), groupName: g.name, marginRight: 15 }))
            )
        );
        this._list.enableRequests = false;
        this._setInitialFocus();
    }

    override _init() {
        this._list._handleLeft = this._handleLeft.bind(this);
        this._list._handleRight = this._handleRight.bind(this);
        this._previewTxHandler = this._onPreviewLoaded.bind(this);
        this._previewTxErrorHandler = this._onPreviewError.bind(this);
        const image = (this.getByRef("Preview")!).tag("Image");
        image.on("txLoaded", this._previewTxHandler);
        image.on("txError", this._previewTxErrorHandler);
    }

    override _detach() {
        this._cancelRetry();
        const image = (this.getByRef("Preview") as any)?.tag("Image");
        if (image) {
            image.off("txLoaded", this._previewTxHandler);
            image.off("txError", this._previewTxErrorHandler);
        }
    }

    private _cancelRetry() {
        if (this._previewRetryTimeout !== null) {
            Registry.clearTimeout(this._previewRetryTimeout);
            this._previewRetryTimeout = null;
        }
    }

    private _onPreviewLoaded() {
        const preview = this.getByRef("Preview")!;
        preview.tag("Image").setSmooth("alpha", 1);
        preview.tag("Placeholder").setSmooth("alpha", 0);
        this._previewRetryCount = 0;
    }

    private _onPreviewError() {
        const url = this._previewUrl;
        if (this._previewRetryCount < 100 && url) {
            this._previewRetryCount++;
            this._previewRetryTimeout = Registry.setTimeout(() => {
                this._previewRetryTimeout = null;
                if (this._previewUrl !== url) return;
                const image = (this.getByRef("Preview")!).tag("Image");
                // Cache-bust the URL so Lightning creates a fresh TextureSource
                // and actually re-fetches from the network instead of reusing
                // the cached error state from the previous attempt.
                image.texture = Img(`${url}&_t=${Date.now()}`).contain(PREVIEW_W, PREVIEW_H);
            }, 1000);
        }
    }

    override _handleLeft() {
        const currentIndex = this._list.index;
        let newIndex = currentIndex - 1;

        while (newIndex >= 0 && this._list.items[newIndex]?.isGroupTitle) {
            newIndex--;
        }

        if (newIndex >= 0) {
            this._list.setIndex(newIndex);
            return true;
        }
        return false;
    }

    override _handleRight() {
        const currentIndex = this._list.index;
        let newIndex = currentIndex + 1;

        while (newIndex < this._list.items.length && this._list.items[newIndex]?.isGroupTitle) {
            newIndex++;
        }

        if (newIndex >= this._list.items.length) {
            for (let i = 0; i < this._list.items.length; i++) {
                if (!this._list.items[i]?.isGroupTitle) {
                    this._list.setIndex(i);
                    return true;
                }
            }
            return false;
        }

        this._list.setIndex(newIndex);
        return true;
    }

    override _unfocus() {
        this._cancelRetry();
        this.getByRef("Preview")!.patch({ smooth: { alpha: 0 } });
    }

    $onChannelFocus(channel: ChannelItemModel, groupName: string) {
        // Always notify parent so WelcomeBox can react to index changes
        this.fireAncestors("$onChannelIndexChanged", this._list.index);

        if (groupName) {
            this.fireAncestors("$onChannelGroupChanged", groupName);
        }

        if (!configs.livetv?.channelPreview?.enabled) return;

        const visibleIndex = Math.min(this._list.index, SCROLL_AFTER - 1);
        const previewX = visibleIndex * (PREVIEW_W + ITEM_SPACING);

        const preview = this.getByRef("Preview") as any;
        const image = preview.tag("Image");
        const logo = preview.tag("Placeholder.Logo");

        // Cancel any pending retry and reset state
        this._cancelRetry();
        this._previewUrl = null;
        this._previewRetryCount = 0;

        // Reset: show placeholder, hide image
        image.alpha = 0.01;
        image.texture = null;
        preview.tag("Placeholder").alpha = 1;

        // Set channel logo on placeholder
        logo.data = channel;

        // Position and show preview container
        preview.x = previewX;
        preview.setSmooth("alpha", 1);

        const tvgId = channel.tvg_id;
        if (!tvgId) return;

        const url = `https://livetv2.alteox.stream/${tvgId}/preview.jpg?height=${PREVIEW_H}&`;
        this._previewUrl = url;
        image.texture = Img(url).contain(PREVIEW_W, PREVIEW_H);
    }

    hidePreview() {
        const preview = this.getByRef("Preview") as any;
        if (!preview) return;
        this._cancelRetry();
        this._previewUrl = null;
        preview.setSmooth("alpha", 0);
    }

    scrollToGroup(groupName: string): void {
        let channelIndex = 0;
        for (const group of this._groups) {
            if (group.name === groupName) {
                this._list.setIndex(channelIndex);
                return;
            }
            channelIndex += group.channels.length;
        }
    }
}
